import { useCallback, useEffect, useMemo, useState } from "react";

import { createArticle, deleteArticle, getArticleNextNumber, getArticles, updateArticle } from "../../../api";
import { logError } from "../../../logging/loggingService";
import { emptyArtikel, toNum } from "./articleUtils";

const getArtikelGroup = (artikel) => {
    const candidate = (artikel?.name || "").trim() || (artikel?.articleNumber || "").trim();
    if (!candidate) {
        return "#";
    }
    const letter = candidate.slice(0, 1).toUpperCase();
    return /^[A-ZÄÖÜ]$/.test(letter) ? letter : "#";
};

export default function useArticleManagement() {
    const [openDialog, setOpenDialog] = useState(false);
    const [artikelList, setArtikelList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    const [currentArtikel, setCurrentArtikel] = useState(emptyArtikel());
    const [editingIndex, setEditingIndex] = useState(null);
    const [deleteAsk, setDeleteAsk] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});

    const [toast, setToast] = useState({ open: false, msg: "", severity: "success" });

    const [query, setQuery] = useState("");
    const [sortBy, setSortBy] = useState("name-asc");

    const net = useMemo(() => toNum(currentArtikel.price), [currentArtikel.price]);
    const rate = useMemo(() => toNum(currentArtikel.taxRate), [currentArtikel.taxRate]);
    const gross = useMemo(() => net * (1 + rate / 100), [net, rate]);

    const fetchArticles = useCallback(() => {
        setLoading(true);
        setErr(null);
        getArticles()
            .then((res) => setArtikelList(res.data || []))
            .catch((e) => {
                logError("Fehler beim Laden der Artikel", e, { component: "ArticleManagement" });
                setErr("Artikel konnten nicht geladen werden.");
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        fetchArticles();
    }, [fetchArticles]);

    const filteredSorted = useMemo(() => {
        const q = query.trim().toLowerCase();
        let list = artikelList;
        if (q) {
            list = list.filter(
                (artikel) =>
                    (artikel.name || "").toLowerCase().includes(q) ||
                    (artikel.articleNumber || "").toLowerCase().includes(q) ||
                    (artikel.description || "").toLowerCase().includes(q)
            );
        }
        const byName = (a, b) => (a.name || "").localeCompare(b.name || "", "de");
        const byPrice = (a, b) => toNum(a.price) - toNum(b.price);
        const byTax = (a, b) => toNum(a.taxRate) - toNum(b.taxRate);

        switch (sortBy) {
            case "name-desc":
                return [...list].sort((a, b) => -byName(a, b));
            case "price-asc":
                return [...list].sort(byPrice);
            case "price-desc":
                return [...list].sort((a, b) => -byPrice(a, b));
            case "tax-asc":
                return [...list].sort(byTax);
            case "tax-desc":
                return [...list].sort((a, b) => -byTax(a, b));
            case "name-asc":
            default:
                return [...list].sort(byName);
        }
    }, [artikelList, query, sortBy]);

    const artikelIndexById = useMemo(() => {
        const map = new Map();
        artikelList.forEach((artikel, index) => {
            if (artikel?.id) {
                map.set(artikel.id, index);
            }
        });
        return map;
    }, [artikelList]);

    const groupedArticles = useMemo(() => {
        const groups = new Map();
        filteredSorted.forEach((artikel) => {
            const group = getArtikelGroup(artikel);
            if (!groups.has(group)) {
                groups.set(group, []);
            }
            groups.get(group).push(artikel);
        });

        const keys = Array.from(groups.keys()).sort((a, b) => {
            if (a === "#") return 1;
            if (b === "#") return -1;
            return a.localeCompare(b, "de");
        });

        return keys.map((key) => ({
            key,
            artikels: groups.get(key),
        }));
    }, [filteredSorted]);

    const resolveArtikelIndex = useCallback(
        (artikel) => {
            if (artikel?.id) {
                const index = artikelIndexById.get(artikel.id);
                if (index !== undefined) {
                    return index;
                }
                return artikelList.findIndex((item) => item?.id === artikel.id);
            }
            return artikelList.findIndex((item) => item === artikel);
        },
        [artikelIndexById, artikelList]
    );

    const resetDialogState = useCallback(() => {
        setCurrentArtikel(emptyArtikel());
        setEditingIndex(null);
        setFieldErrors({});
    }, []);

    const handleDialogOpen = useCallback(async () => {
        try {
            const res = await getArticleNextNumber();
            setCurrentArtikel({ ...emptyArtikel(), articleNumber: res.data });
            setEditingIndex(null);
            setFieldErrors({});
            setOpenDialog(true);
        } catch (e) {
            logError("Fehler beim Abrufen der nächsten Artikelnummer", e, { component: "ArticleManagement" });
            setToast({ open: true, msg: "Nächste Artikelnummer konnte nicht geladen werden.", severity: "error" });
            setCurrentArtikel({ ...emptyArtikel() });
            setFieldErrors({});
            setOpenDialog(true);
        }
    }, []);

    const handleDialogClose = useCallback(() => {
        setOpenDialog(false);
        resetDialogState();
    }, [resetDialogState]);

    const handleEdit = useCallback(
        (index) => {
            setCurrentArtikel(artikelList[index]);
            setEditingIndex(index);
            setFieldErrors({});
            setOpenDialog(true);
        },
        [artikelList]
    );

    const handleDeleteAsk = useCallback((artikel, index) => setDeleteAsk({ artikel, index }), []);

    const handleDeleteConfirm = useCallback(async () => {
        if (!deleteAsk) return;
        const prev = artikelList;
        setArtikelList((list) => list.filter((_, i) => i !== deleteAsk.index));
        try {
            await deleteArticle(deleteAsk.artikel.id);
            setToast({ open: true, msg: "Artikel gelöscht.", severity: "success" });
        } catch (e) {
            logError("Fehler beim Löschen des Artikels", e, { component: "ArticleManagement" });
            setToast({ open: true, msg: "Löschen fehlgeschlagen.", severity: "error" });
            setArtikelList(prev);
        } finally {
            setDeleteAsk(null);
        }
    }, [artikelList, deleteAsk]);

    const handleFieldChange = useCallback((field, value) => {
        setCurrentArtikel((prev) => ({ ...prev, [field]: value }));
        setFieldErrors((prev) => {
            if (!prev[field]) return prev;
            const next = { ...prev };
            delete next[field];
            return next;
        });
    }, []);

    const handleToggleHasAmount = useCallback((value) => {
        setCurrentArtikel((state) => ({ ...state, hasAmount: value }));
    }, []);

    const handleSave = useCallback(async () => {
        if (!currentArtikel.name?.trim()) {
            setFieldErrors({ name: "Bitte Artikelname angeben." });
            setToast({ open: true, msg: "Bitte prüfe die markierten Felder.", severity: "warning" });
            return;
        }
        const payload = {
            ...currentArtikel,
            price: toNum(currentArtikel.price),
            taxRate: toNum(currentArtikel.taxRate),
            amount: currentArtikel.hasAmount && currentArtikel.amount !== "" ? toNum(currentArtikel.amount) : "",
        };

        try {
            if (editingIndex !== null) {
                const res = await updateArticle(payload.id, payload);
                setArtikelList((list) => {
                    const next = [...list];
                    next[editingIndex] = res.data;
                    return next;
                });
                setToast({ open: true, msg: "Artikel aktualisiert.", severity: "success" });
            } else {
                const res = await createArticle(payload);
                setArtikelList((list) => [...list, res.data]);
                setToast({ open: true, msg: "Artikel angelegt.", severity: "success" });
            }
            handleDialogClose();
        } catch (e) {
            logError("Fehler beim Speichern des Artikels", e, { component: "ArticleManagement" });
            const status = e?.response?.status;
            const backendError = e?.response?.data;

            if (status === 409 && backendError?.suggestion) {
                setFieldErrors({ articleNumber: `${backendError.error} Vorschlag: ${backendError.suggestion}` });
                setCurrentArtikel((prev) => ({ ...prev, articleNumber: backendError.suggestion }));
                setToast({
                    open: true,
                    msg: "Artikelnummer angepasst. Bitte erneut speichern.",
                    severity: "info",
                });
                return;
            }

            const details = Array.isArray(backendError?.details) ? backendError.details : null;
            if (details && details.length > 0) {
                const nextErrors = {};
                details.forEach((detail) => {
                    const rawField = detail?.field;
                    const message = detail?.message;
                    if (!rawField || !message) return;
                    const field = rawField.split(".").pop();
                    nextErrors[field] = message;
                });
                if (Object.keys(nextErrors).length > 0) {
                    setFieldErrors(nextErrors);
                    setToast({
                        open: true,
                        msg: backendError?.message || "Bitte prüfe die markierten Felder.",
                        severity: "error",
                    });
                    return;
                }
            }
            setToast({ open: true, msg: "Speichern fehlgeschlagen.", severity: "error" });
        }
    }, [currentArtikel, editingIndex, handleDialogClose]);

    return {
        openDialog,
        artikelList,
        loading,
        err,
        currentArtikel,
        editingIndex,
        deleteAsk,
        fieldErrors,
        toast,
        query,
        sortBy,
        net,
        gross,
        filteredSorted,
        groupedArticles,
        resolveArtikelIndex,
        setQuery,
        setSortBy,
        setToast,
        setDeleteAsk,
        fetchArticles,
        handleDialogOpen,
        handleDialogClose,
        handleEdit,
        handleDeleteAsk,
        handleDeleteConfirm,
        handleFieldChange,
        handleToggleHasAmount,
        handleSave,
    };
}
